import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Api } from '../api/index.ts';
import type { UnreadCount } from '../api/NotificationsApi.ts';

/**
 * Unread notifications per organization, for the badges (sidebar, tab bar,
 * org switcher). Refreshed when the shell opens, when the server says they
 * changed, and after marking read. Badges are best effort: a failed refresh
 * keeps the last counts.
 */
export const useInboxStore = defineStore('inbox', () => {
  const counts = ref<UnreadCount[]>([]);
  let api: Api | null = null;
  /** Only the latest refresh may write (an older, slower answer is stale). */
  let latest = 0;

  function bind(apiClients: Api): void {
    api = apiClients;
  }

  async function refresh(): Promise<void> {
    if (!api) return;
    const mine = ++latest;
    try {
      const { orgs } = await api.notifications.unread();
      if (mine === latest) counts.value = orgs;
    } catch {
      // Keep the last counts; the next change or reconnect refreshes them.
    }
  }

  function countFor(slug: string | null): number {
    return counts.value.find((c) => c.slug === slug)?.count ?? 0;
  }

  /** Unread notifications in orgs other than `slug` (the switcher's dot). */
  function elsewhere(slug: string | null): boolean {
    return counts.value.some((c) => c.slug !== slug && c.count > 0);
  }

  function reset(): void {
    counts.value = [];
    latest++;
  }

  return { counts, bind, refresh, countFor, elsewhere, reset };
});
